import { useEffect, useState } from "react";
import { call as rpc } from "../../../common/jsonRpcRoutes";

interface ConfirmationRewardsOptions {
  /**
   * Booking email to check for an existing DesignMyNight account. Omit to skip
   * the check (for example when the guest is already known to be signed in).
   */
  readonly email?: string;
  /** When false no requests are made — used when the banner is not shown at all. */
  readonly enabled?: boolean;
}

interface ConfirmationRewardsState {
  /** Full URL of the guest's DesignMyNight rewards page, when configured. */
  rewardsUrl?: string;
  /**
   * Whether the booking email already has a DesignMyNight account. Undefined
   * while unknown — because the check is still running, errored, or there was
   * no email to check — so callers can avoid claiming the guest is signed in.
   */
  hasAccount?: boolean;
  /** True once we know the booking email has no DesignMyNight account. */
  canSignUp: boolean;
}

/**
 * Decide whether the confirmation should offer account creation, and resolve
 * the URL of the guest's DesignMyNight rewards page.
 *
 * The booking's `user` cannot be used to detect an account. Collins attaches a
 * client record to every booking, so `user.user_id` is populated even for a
 * guest with no DesignMyNight account — keying off it suppresses the sign-up
 * for everyone. Instead the booking email is checked against DMN directly,
 * mirroring the legacy booking form's `emailExists` guard.
 *
 * Fails closed: if the check errors we do not offer sign-up, so a guest is
 * never shown a form that would immediately fail with "email already
 * registered".
 */
export function useConfirmationRewards({
  email,
  enabled = true,
}: ConfirmationRewardsOptions): ConfirmationRewardsState {
  const [rewardsUrl, setRewardsUrl] = useState<string | undefined>(undefined);
  const [hasAccount, setHasAccount] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    // Promise.resolve guards against a caller stubbing the RPC layer with a
    // non-promise return.
    Promise.resolve(rpc("/rewards/config", {}))
      .then((config) => {
        if (!cancelled && config?.rewardsUrl) setRewardsUrl(config.rewardsUrl);
      })
      .catch((err) => {
        // The balance link is optional detail; the message still renders.
        console.warn("[useConfirmationRewards] Failed to load rewards config:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !email) {
      setHasAccount(undefined);
      return;
    }

    let cancelled = false;

    Promise.resolve(rpc("/checkEmailAccount", { email }))
      .then((result) => {
        if (!cancelled && typeof result?.exists === "boolean") setHasAccount(result.exists);
      })
      .catch((err) => {
        console.warn("[useConfirmationRewards] Failed to check for an existing account:", err);
        if (!cancelled) setHasAccount(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [email, enabled]);

  return { rewardsUrl, hasAccount, canSignUp: hasAccount === false };
}
